import { supabase } from "@/lib/supabase";

const TIER_LIMITS: Record<
    string,
    { mt_chars: number; mt_chars_daily: number; ai_queries: number }
> = {
    free: { mt_chars: 50_000, mt_chars_daily: 5_000, ai_queries: 100 },
    standard: { mt_chars: 200_000, mt_chars_daily: 15_000, ai_queries: 500 },
    premium: { mt_chars: 500_000, mt_chars_daily: 50_000, ai_queries: 3000 },
};

export function getLimitsForTier(tier?: string) {
    return TIER_LIMITS[tier || "free"] ?? TIER_LIMITS["free"];
}

// Keep these exports so existing imports don't break
export const FREE_MT_LIMIT = TIER_LIMITS["free"].mt_chars;
export const FREE_AI_LIMIT = TIER_LIMITS["free"].ai_queries;

export interface UserUsage {
    mt_chars_used: number;
    ai_queries_used: number;
    period_start: string;
    mt_chars_used_today: number;
    mt_chars_extra: number;
    last_daily_reset: string | null;
    mt_chars_deepl?: number;
    mt_chars_google?: number;
    mt_chars_mymemory?: number;
    storage_used_bytes?: number;
}

export async function getProviderUsage(
    userId: string,
): Promise<{ deepl: number; google: number; mymemory: number }> {
    const { data } = await supabase
        .from("user_usage")
        .select("mt_chars_deepl, mt_chars_google, mt_chars_mymemory")
        .eq("user_id", userId)
        .single();
    return {
        deepl: data?.mt_chars_deepl || 0,
        google: data?.mt_chars_google || 0,
        mymemory: data?.mt_chars_mymemory || 0,
    };
}

// Read-only. The server (consume_usage in the API) is the only thing that counts and
// resets MT and AI usage; this just shows the numbers. It applies the same month and day
// rollover rules in memory so a new day or month displays as zero before the next MT call.
// Both are decided in UTC, like the server.
export async function getUsage(userId: string): Promise<UserUsage> {
    const now = new Date();
    const periodStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
    ).toISOString();
    const today = now.toISOString().split("T")[0];

    const { data } = await supabase
        .from("user_usage")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

    // No row yet: the server creates it on the first MT or AI request.
    if (!data) {
        return {
            mt_chars_used: 0,
            ai_queries_used: 0,
            period_start: periodStart,
            mt_chars_used_today: 0,
            mt_chars_extra: 0,
            last_daily_reset: null,
        };
    }

    const storedPeriod = new Date(data.period_start);
    const isNewMonth =
        storedPeriod.getUTCMonth() !== now.getUTCMonth() ||
        storedPeriod.getUTCFullYear() !== now.getUTCFullYear();
    const isNewDay = !data.last_daily_reset || data.last_daily_reset !== today;

    return {
        ...data,
        mt_chars_used: isNewMonth ? 0 : data.mt_chars_used || 0,
        ai_queries_used: isNewMonth ? 0 : data.ai_queries_used || 0,
        period_start: isNewMonth ? periodStart : data.period_start,
        mt_chars_used_today: isNewDay ? 0 : data.mt_chars_used_today || 0,
        last_daily_reset: isNewDay ? today : data.last_daily_reset,
        mt_chars_extra: data.mt_chars_extra || 0,
    };
}

export async function incrementStorageUsage(
    userId: string,
    bytes: number,
): Promise<void> {
    try {
        const { data } = await supabase
            .from("user_usage")
            .select("storage_used_bytes")
            .eq("user_id", userId)
            .single();
        const current = data?.storage_used_bytes || 0;
        await supabase
            .from("user_usage")
            .update({
                storage_used_bytes: current + bytes,
                updated_at: new Date().toISOString(),
            })
            .eq("user_id", userId);
    } catch (err) {
        console.warn("incrementStorageUsage error:", err);
    }
}
